import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { AU_RADIUS_CONTROL } from '@/theme/au-compat';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FC, useId } from 'react';

interface ProgressBarProps {
  value?: number;
  label?: string;
  /**
   * Velocidad de la animación de las franjas. **`0` las congela**: la operación llegó a un estado
   * terminal y la animación *es* la señal de que la compuerta se está moviendo ahora.
   */
  speed?: number;
  /**
   * El color del canal **marca** del estado (`getStateColor`), que se propaga por `color` de la
   * raíz: el relleno, el filo y el halo lo heredan con `currentColor` en vez de recibirlo cada uno
   * por su lado.
   */
  color: string;
  indeterminate?: boolean;
}

export const ProgressBar: FC<ProgressBarProps> = ({
  label,
  speed = 3,
  color,
  indeterminate,
  value = 0,
}) => {
  const { formatMetricValue } = useFormatters();
  const uniqueId = useId();
  /* El id termina dentro de un `url(#…)`: `useId` devuelve `_R_1_` en React 19 y `:r1:` en la 18,
     y los dos puntos no son válidos ahí. Se deja sólo lo que es seguro en un fragmento en vez de
     confiar en el formato de la versión de turno. */
  const patternId = `progress-${uniqueId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const height = 26;
  const patternWidth = 14;
  const barWidth = 10;
  const skew = -20;

  return (
    // `.oc-progress` — sin padding propio: el del área de operación alcanza, y el que tenía
    // desalineaba la barra contra el recuadro de posición de arriba, que sí arranca en el borde.
    <Box sx={{ width: '100%', minWidth: 0, color }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
        {!indeterminate && (
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {formatMetricValue(value, { unit: MetricUnit.Percent, precision: 0 })}
          </Typography>
        )}
      </Box>

      {/* `.oc-progress-track`: el filo y el halo salen del mismo `currentColor` que el relleno, así
          que la caja entera cambia de color con el estado sin que nadie le pase un segundo valor. */}
      <Box
        sx={{
          border: '2px solid color-mix(in srgb, currentColor 30%, transparent)',
          borderRadius: AU_RADIUS_CONTROL,
          height: '34px',
          p: '3px',
          boxSizing: 'border-box',
          boxShadow: '0 0 12px -4px color-mix(in srgb, currentColor 25%, transparent)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <svg width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
          <defs>
            <pattern
              id={patternId}
              x="0"
              y="0"
              width={patternWidth}
              height={height}
              patternUnits="userSpaceOnUse"
              patternTransform={`skewX(${skew})`}
            >
              <rect x="0" y="0" width={barWidth} height={height} fill="currentColor" opacity="0.9" />
              {/* Sin guarda, `speed: 0` daba `dur="Infinity s"` en vez de congelar el patrón. */}
              {speed > 0 && (
                <animate
                  attributeName="x"
                  from="0"
                  to={patternWidth.toString()}
                  dur={`${8 / speed}s`}
                  calcMode="linear"
                  repeatCount="indefinite"
                />
              )}
            </pattern>
          </defs>

          {indeterminate ? (
            <rect y="0" height="100%" rx="4" fill="currentColor" opacity="0.6">
              <animate
                attributeName="x"
                values="0%; 90%; 0%"
                keyTimes="0; 0.35; 1"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                dur="3s"
                repeatCount="indefinite"
              />

              <animate
                attributeName="width"
                values="10%; 20%; 10%; 20%; 10%"
                keyTimes="0; 0.17; 0.35; 0.675; 1"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
                dur="3s"
                repeatCount="indefinite"
              />
            </rect>
          ) : (
            <rect
              x="0"
              y="0"
              width={`${Math.min(Math.max(value, 0), 100)}%`}
              height="100%"
              fill={`url(#${patternId})`}
              style={{ transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
          )}
        </svg>
      </Box>
    </Box>
  );
};
